import React, { useState, useEffect } from 'react';
import { Row, Col, Empty, Skeleton, Typography, Space, Button, Tabs, Pagination } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import auctionService from '../../services/auctionService';
import favoriteService from '../../services/favoriteService';
import { message } from 'antd';
import { toggleFavoriteAuction } from '../../redux/userSlice';
import AuctionCard from './AuctionCard';
import AuctionCardSkeleton from './AuctionCardSkeleton';
import FeaturedActiveAuctions from './FeaturedActiveAuctions';
import AuctionSearch from './AuctionSearch';
import CategorySection from './CategorySection';
import AuctionCountdown from '../Common/AuctionCountdown';
import moment from 'moment';
import './AuctionList.css';
import { AppstoreOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const AuctionList = () => {
    const [auctions, setAuctions] = useState([]);
    const [upcomingAuctions, setUpcomingAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState({
        status: 'all',
        category: 'all'
    });
    const [categories, setCategories] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });
    const { currentUser } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [viewMode, setViewMode] = useState('grid');
    const [upcomingCategory, setUpcomingCategory] = useState('today');

    // Read URL parameters when component mounts or URL changes
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const searchQuery = searchParams.get('q'); // Changed from 'search' to 'q' for consistency
        const statusQuery = searchParams.get('status');
        const categoryQuery = searchParams.get('category');

        setSearchInput(searchQuery || '');
        setFilters({
            status: statusQuery || 'all',
            category: categoryQuery || 'all'
        });
    }, [location.search]);

    // Load initial data when component mounts
    useEffect(() => {
        loadData(filters, pagination.current);
        loadUpcomingAuctionsData();
    }, []);

    // Remove immediate filter application - filters only apply on search
    // useEffect(() => {
    //     loadData();
    // }, [filters]);

    // Sync local state with Redux favorites
    useEffect(() => {
        if (currentUser) {
            // Ensure favorite_auctions is initialized
            const favoriteAuctions = currentUser.favorite_auctions || [];
            setAuctions(prevAuctions => 
                prevAuctions.map(auction => ({
                    ...auction,
                    is_favorited: favoriteAuctions.includes(auction.id)
                }))
            );
        }
    }, [currentUser?.favorite_auctions]);

    const loadData = async (filtersToUse = null, page = 1) => {
        try {
            setLoading(true);
            
            const currentFilters = filtersToUse || filters;
            
            // Fetch auctions and categories in parallel using Promise.all
            const [auctionsResponse, categoriesResponse] = await Promise.all([
                auctionService.getAllAuctions({
                    ...currentFilters,
                    status: currentFilters.status === 'all' ? undefined : currentFilters.status.toLowerCase(),
                    category: currentFilters.category === 'all' ? undefined : currentFilters.category.toLowerCase(),
                    search: searchInput || undefined,
                    page: page,
                    per_page: pagination.pageSize
                }),
                auctionService.getCategories()
            ]);

            // Process auctions with favorites
            const favoriteAuctions = currentUser?.favorite_auctions || [];
            const auctionsWithFavorites = auctionsResponse.data.map(auction => ({
                ...auction,
                is_favorited: favoriteAuctions.includes(auction.id)
            }));

            setAuctions(auctionsWithFavorites);
            setCategories(categoriesResponse.data);
            
            // Update pagination from response meta
            // Laravel pagination automatically includes meta with current_page, total, per_page, etc.
            if (auctionsResponse.meta) {
                setPagination(prev => ({
                    ...prev,
                    current: auctionsResponse.meta.current_page || page,
                    total: auctionsResponse.meta.total || 0,
                    pageSize: auctionsResponse.meta.per_page || prev.pageSize
                }));
            } else {
                // Fallback: if no meta, assume all data is loaded (no pagination)
                setPagination(prev => ({
                    ...prev,
                    current: 1,
                    total: auctionsWithFavorites.length,
                    pageSize: auctionsWithFavorites.length || 10
                }));
            }
        } catch (error) {
            console.error('Error loading data:', error);
            message.error('Failed to load auctions');
        } finally {
            setLoading(false);
        }
    };

    const loadUpcomingAuctionsData = async () => {
        try {
            setUpcomingLoading(true);
            const auctions = await loadAllUpcomingAuctions();
            setUpcomingAuctions(auctions);
        } catch (error) {
            console.error('Error loading upcoming auctions:', error);
        } finally {
            setUpcomingLoading(false);
        }
    };

    // Removed handleFilterChange - filters only apply on search button click

    const handleSearch = async (value, searchFilters = null) => {
        const filtersToUse = searchFilters || filters;
        
        // Update local state first
        setSearchInput(value);
        setFilters(filtersToUse);
        // Reset to first page when searching
        setPagination(prev => ({ ...prev, current: 1 }));
        
        // Always redirect to search results page when search button is clicked
        // This ensures blank searches show all auctions on the search results page
        const searchParams = new URLSearchParams();
        if (value && value.trim()) {
            searchParams.set('q', value.trim());
        }
        if (filtersToUse.status !== 'all') searchParams.set('status', filtersToUse.status);
        if (filtersToUse.category !== 'all') searchParams.set('category', filtersToUse.category);
        
        navigate({
            pathname: '/search-results',
            search: searchParams.toString()
        });
    };

    const handlePaginationChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
        loadData(filters, page);
    };

    const handleCategoryChange = (category) => {
        // Category changes are now also handled only when search button is clicked
        const newFilters = { ...filters, category };
        setFilters(newFilters);
        // Don't apply immediately - wait for search button click
    };

    const handleFavoriteToggle = async (auctionId) => {
        if (!currentUser) {
            message.warning('Please login to add favorites');
            return;
        }

        // Find the auction to get current favorite state
        const auction = auctions.find(a => a.id === auctionId) || 
                       upcomingAuctions.find(a => a.id === auctionId);
        const currentFavoriteState = auction?.is_favorited || false;

        // Optimistically update local state for regular auctions
        setAuctions(prevAuctions => 
            prevAuctions.map(auction => 
                auction.id === auctionId 
                    ? { ...auction, is_favorited: !auction.is_favorited }
                    : auction
            )
        );

        // Optimistically update local state for upcoming auctions
        setUpcomingAuctions(prevAuctions => 
            prevAuctions.map(auction => 
                auction.id === auctionId 
                    ? { ...auction, is_favorited: !auction.is_favorited }
                    : auction
            )
        );

        // Optimistically update Redux state
        dispatch(toggleFavoriteAuction(auctionId));

        // Make API call to persist the favorite
        try {
            await favoriteService.toggleFavoriteAuction(auctionId);
        } catch (error) {
            console.error('Error toggling favorite:', error);
            // Revert the optimistic updates on error
            setAuctions(prevAuctions => 
                prevAuctions.map(auction => 
                    auction.id === auctionId 
                        ? { ...auction, is_favorited: currentFavoriteState }
                        : auction
                )
            );
            setUpcomingAuctions(prevAuctions => 
                prevAuctions.map(auction => 
                    auction.id === auctionId 
                        ? { ...auction, is_favorited: currentFavoriteState }
                        : auction
                )
            );
            dispatch(toggleFavoriteAuction(auctionId)); // Revert Redux state
            message.error(error.response?.data?.message || 'Failed to update favorite status');
        }
    };

    // Removed handleCountdownComplete - no longer refreshing on countdown complete
    // const handleCountdownComplete = (auction) => {
    //     // Refresh the auction data when countdown completes
    //     loadData(filters);
    // };

    // Load all upcoming active auctions from backend
    const loadAllUpcomingAuctions = async () => {
        try {
            const response = await auctionService.getUpcomingActiveAuctions();
            
            // Process auctions with favorites
            const favoriteAuctions = currentUser?.favorite_auctions || [];
            const auctionsWithFavorites = response.data.map(auction => ({
                ...auction,
                is_favorited: favoriteAuctions.includes(auction.id)
            }));
            
            return auctionsWithFavorites;
        } catch (error) {
            console.error('Error loading upcoming auctions:', error);
            message.error('Failed to load upcoming auctions');
            return [];
        }
    };

    // Filter upcoming auctions by category locally
    const getUpcomingAuctionsByCategory = (category = 'today') => {
        const now = moment();
        const today = moment().startOf('day');
        const tomorrow = moment().add(1, 'day').startOf('day');

        if (category === 'today') {
            // Show auctions starting today
            return upcomingAuctions.filter(auction => {
                const startTime = moment(auction.start_time);
                return startTime.isSameOrAfter(today) && startTime.isBefore(tomorrow);
            }).slice(0, 6);
        }

        // Filter by category
        return upcomingAuctions.filter(auction => {
            const auctionCategory = auction.category?.name || auction.category || auction.category_name;
            return auctionCategory && auctionCategory.toLowerCase() === category.toLowerCase();
        }).slice(0, 6);
    };

    const handleUpcomingCategoryChange = (category) => {
        setUpcomingCategory(category);
    };

    // Get categories that have upcoming auctions
    const getCategoriesWithAuctions = () => {
        // Get unique categories from upcoming auctions
        const categoriesWithAuctions = new Set();
        
        upcomingAuctions.forEach(auction => {
            const auctionCategory = auction.category?.name || auction.category || auction.category_name;
            if (auctionCategory) {
                categoriesWithAuctions.add(auctionCategory.toLowerCase());
            }
        });

        // Filter categories to only include those with auctions
        return categories.filter(category => 
            categoriesWithAuctions.has(category.name.toLowerCase())
        );
    };

    return (
        <div className="auction-list">
            <AuctionSearch
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                filters={filters}
                setFilters={setFilters}
                loading={loading}
                auctionCount={auctions.length}
                onSearch={handleSearch}
            />

            {/* Category Section */}
            <CategorySection
                categories={categories}
                selectedCategory={filters.category}
                onCategoryChange={handleCategoryChange}
                loading={loading}
            />

            {/* Featured Active Auctions Section (now below search) */}
            <FeaturedActiveAuctions />

            {/* Upcoming Auctions Section */}
            <div className="upcoming-auctions-section">
                {/* Header */}
                <div style={{ 
                    marginBottom: 24,
                    padding: window.innerWidth <= 768 ? '16px' : '24px',
                    background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(114, 46, 209, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background decoration */}
                    <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(114, 46, 209, 0.1) 0%, transparent 70%)',
                        animation: 'float 6s ease-in-out infinite'
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: window.innerWidth <= 768 ? 8 : 12, 
                            marginBottom: 8
                        }}>
                            <ClockCircleOutlined style={{ 
                                fontSize: window.innerWidth <= 768 ? '20px' : '24px', 
                                color: 'white',
                                flexShrink: 0
                            }} />
                            <Title level={3} style={{ 
                                margin: 0, 
                                color: 'white',
                                fontWeight: 800,
                                fontSize: window.innerWidth <= 576 ? '18px' : window.innerWidth <= 768 ? '20px' : '24px',
                                lineHeight: 1.2
                            }}>
                                Upcoming Auctions
                            </Title>
                        </div>
                        <Text style={{ 
                            fontSize: window.innerWidth <= 576 ? '13px' : window.innerWidth <= 768 ? '14px' : '16px',
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontWeight: 500,
                            lineHeight: 1.5
                        }}>
                            Discover auctions starting soon across different categories
                        </Text>
                    </div>
                </div>

                {/* Category Tabs */}
                <div style={{ marginBottom: 24 }}>
                    <Tabs
                        activeKey={upcomingCategory}
                        onChange={handleUpcomingCategoryChange}
                        items={[
                            {
                                key: 'today',
                                label: 'Today',
                                children: null
                            },
                            ...getCategoriesWithAuctions().map(category => ({
                                key: category.name.toLowerCase(),
                                label: category.name,
                                children: null
                            }))
                        ]}
                        style={{
                            '& .ant-tabs-tab': {
                                fontSize: '16px',
                                fontWeight: 500,
                                padding: '12px 20px'
                            }
                        }}
                    />
                </div>

                {upcomingLoading ? (
                    <div className="custom-auctions-list">
                        {[1, 2, 3, 4, 5, 6].map((item) => (
                            <div key={item} className="auction-list-item">
                                <div className="auction-list-content">
                                    <div className="auction-icon">
                                        <div className="skeleton-icon"></div>
                                    </div>
                                    <div className="auction-details">
                                        <div className="skeleton-title"></div>
                                        <div className="skeleton-subtitle"></div>
                                    </div>
                                    <div className="auction-time">
                                        <div className="skeleton-time"></div>
                                        <div className="skeleton-countdown"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="custom-auctions-list">
                        {getUpcomingAuctionsByCategory(upcomingCategory).map((auction, index) => (
                            <div key={auction.id} className="auction-list-item">
                                <div className="auction-list-content">
                                    {/* Category Icon */}
                                    <div className="auction-icon">
                                        <div className="category-icon-wrapper">
                                            {upcomingCategory === 'today' ? (
                                                <ClockCircleOutlined className="category-icon" />
                                            ) : (
                                                <AppstoreOutlined className="category-icon" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Auction Details */}
                                    <div className="auction-details">
                                        <div 
                                            className="auction-title clickable-title"
                                            onClick={() => navigate(`/auctions/${auction.slug || auction.id}`)}
                                        >
                                            {auction.title}
                                        </div>
                                        <div className="auction-subtitle">
                                     
                                            <span>{auction.category?.name || auction.category || 'General'}</span>
                                            {auction.location && (
                                                <>
                                                    <span className="location-separator">•</span>
                                                    <span className="location-text">{auction.location}</span>
                                                </>
                                            )}
                                            {auction.lots_count && (
                                                <>
                                                    <span>•</span>
                                                    <span>{auction.lots_count} Lots</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Time Information */}
                                    <div className="auction-time">
                                        <div className="time-info">
                                            <div className="date-display">
                                                {moment(auction.start_time).isSame(moment(), 'day') ? (
                                                    <span className="today-text">TODAY</span>
                                                ) : (
                                                    <span className="date-text">
                                                        {moment(auction.start_time).format('Do MMM')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="time-display">
                                                {moment(auction.start_time).format('h:mm A')}
                                            </div>
                                        </div>
                                        <div className="countdown-display">
                                            <AuctionCountdown
                                                auction={auction}
                                                variant="text"
                                            />
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <div className="auction-action">
                                        <Button
                                            type="primary"
                                            size="small"
                                            className="action-btn"
                                            onClick={() => {
                                                // Determine action based on auction status
                                                const now = moment();
                                                const startTime = moment(auction.start_time);
                                                const isToday = startTime.isSame(moment(), 'day');
                                                
                                                if (auction.status === 'completed') {
                                                    // Auction is completed - show results
                                                    navigate(`/auctions/${auction.slug || auction.id}`);
                                                } else if (isToday && auction.status === 'active') {
                                                    // Auction is today and active - open bidding in popup
                                                    const newWindow = window.open(
                                                        `/live-auctions/${auction.slug || auction.id}`,
                                                        '_blank',
                                                        'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1920,height=1080'
                                                    );
                                                    // Focus the new window
                                                    if (newWindow) {
                                                        newWindow.focus();
                                                    }
                                                } else if (startTime.isAfter(now)) {
                                                    // Auction hasn't started yet - show auction details
                                                    navigate(`/auctions/${auction.slug || auction.id}`);
                                                } else if (auction.status === 'active') {
                                                    // Auction has started and is active - open live auction page
                                                    const newWindow = window.open(
                                                        `/live-auctions/${auction.slug || auction.id}`,
                                                        '_blank',
                                                        'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=1920,height=1080'
                                                    );
                                                    // Focus the new window
                                                    if (newWindow) {
                                                        newWindow.focus();
                                                    }
                                                } else {
                                                    // Auction has started but not active - show auction details
                                                    navigate(`/auctions/${auction.slug || auction.id}`);
                                                }
                                            }}
                                        >
                                            {(() => {
                                                const now = moment();
                                                const startTime = moment(auction.start_time);
                                                const isToday = startTime.isSame(moment(), 'day');
                                                
                                                if (auction.status === 'completed') {
                                                    return 'View Auction';
                                                } else if (isToday && auction.status === 'active') {
                                                    return 'Enter Auction';
                                                } else if (startTime.isAfter(now)) {
                                                    return 'View Auction';
                                                } else if (auction.status === 'active') {
                                                    // Auction has started and is active
                                                    return 'Enter Auction';
                                                } else {
                                                    // Auction has started but not active
                                                    return 'View Auction';
                                                }
                                            })()}
                                        </Button>
                                    </div>

                                    {/* Favorite Button */}
                                    <div className="auction-favorite">
                                        <Button
                                            type="text"
                                            icon={auction.is_favorited ? '★' : '☆'}
                                            onClick={() => handleFavoriteToggle(auction.id)}
                                            className={`favorite-btn ${auction.is_favorited ? 'favorited' : ''}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {getUpcomingAuctionsByCategory(upcomingCategory).length === 0 && (
                            <div className="custom-auctions-list">
                                <div className="auction-list-item">
                                    <div className="auction-list-content">
                                        {/* Empty State Icon */}
                                        <div className="auction-icon">
                                            <div className="category-icon-wrapper empty-state-icon">
                                                <ClockCircleOutlined className="category-icon" />
                                            </div>
                                        </div>

                                        {/* Empty State Details */}
                                        <div className="auction-details">
                                            <div className="auction-title empty-state-title">
                                                No upcoming auctions
                                            </div>
                                            <div className="auction-subtitle empty-state-subtitle">
                                                {upcomingCategory === 'today' 
                                                    ? "No auctions are scheduled to start today. Check back tomorrow or try selecting a different category."
                                                    : `No upcoming auctions found in the ${upcomingCategory} category. Try selecting a different category.`
                                                }
                                            </div>
                                        </div>

                                        {/* Empty State Time - Hidden */}
                                        <div className="auction-time empty-time-section">
                                            <div className="time-info">
                                                <div className="date-display">
                                                    <span className="date-text empty-text">No time</span>
                                                </div>
                                                <div className="time-display empty-time">
                                                    Available soon
                                                </div>
                                            </div>
                                            <div className="countdown-display">
                                                Check back later
                                            </div>
                                        </div>

                                        {/* Empty Action Button */}
                                        <div className="auction-action">
                                            <Button
                                                type="default"
                                                size="small"
                                                className="action-btn empty-action-btn"
                                                disabled
                                            >
                                                No Action
                                            </Button>
                                        </div>

                                        {/* Empty Favorite Button - Hidden */}
                                        <div className="auction-favorite">
                                            <div className="favorite-btn empty-favorite">
                                                ☆
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Regular Auctions Section */}
            <div className="regular-auctions-section">
                {/* Header */}
                <div style={{ 
                    marginBottom: 24,
                    padding: window.innerWidth <= 768 ? '16px' : '24px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(82, 196, 26, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    
                    {/* Background decoration */}
                    <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(82, 196, 26, 0.1) 0%, transparent 70%)',
                        animation: 'float 6s ease-in-out infinite'
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            flexWrap: 'wrap', 
                            gap: 12,
                            marginBottom: 8
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 768 ? 8 : 12 }}>
                            <AppstoreOutlined style={{ 
                                fontSize: window.innerWidth <= 768 ? '20px' : '24px', 
                                color: 'white',
                                flexShrink: 0
                            }} />
                            <Title level={3} style={{ 
                                margin: 0, 
                                color: 'white',
                                fontWeight: 800,
                                fontSize: window.innerWidth <= 576 ? '18px' : window.innerWidth <= 768 ? '20px' : '24px',
                                lineHeight: 1.2
                            }}>
                                All Auctions
                            </Title>
                            </div>
                            <Button
                                type="primary"
                                onClick={() => {
                                    // Navigate to search results page with all auctions
                                    navigate('/search-results');
                                }}
                                style={{
                                    fontSize: window.innerWidth <= 576 ? '12px' : window.innerWidth <= 768 ? '13px' : '14px',
                                    fontWeight: 600,
                                    height: window.innerWidth <= 576 ? '36px' : window.innerWidth <= 768 ? '38px' : '40px',
                                    padding: window.innerWidth <= 576 ? '0 16px' : '0 20px',
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(31, 41, 55, 0.3)',
                                    transition: 'all 0.3s ease',
                                    color: 'white',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(31, 41, 55, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(31, 41, 55, 0.3)';
                                }}
                            >
                                View All Auctions
                            </Button>
                        </div>
                        <Text style={{ 
                            fontSize: window.innerWidth <= 576 ? '13px' : window.innerWidth <= 768 ? '14px' : '16px',
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontWeight: 500,
                            lineHeight: 1.5
                        }}>
                            Browse and discover all available auctions
                        </Text>
                    </div>
                </div>

                {loading ? (
                    <div className="auctions-grid">
                        <Row gutter={[32, 32]}>
                            {[1, 2, 3, 4, 5, 6,7,8,9, 10, 11, 12].map((item) => (
                                <Col xs={24} sm={12} md={8} key={item}>
                                    <AuctionCardSkeleton viewMode={viewMode} />
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : auctions.length === 0 ? (
                    <div className="empty-container">
                        <div className="empty-card">
                            <div style={{ 
                                fontSize: '64px', 
                                marginBottom: '24px',
                                opacity: 0.3
                            }}>
                                🏆
                            </div>
                            <Text className="empty-title">
                                No auctions found
                            </Text>
                            <Text className="empty-description">
                                No auctions are currently available. Check back later for new auctions or try adjusting your filters.
                            </Text>
                            <div style={{ marginTop: '24px' }}>
                                <Button 
                                    type="primary" 
                                    onClick={() => window.location.reload()}
                                    style={{
                                        borderRadius: '8px',
                                        height: '40px',
                                        padding: '0 24px',
                                        fontWeight: 500
                                    }}
                                >
                                    Refresh Page
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="auctions-grid">
                            <Row gutter={[32, 32]}>
                                {auctions.map(auction => (
                                    <Col xs={24} sm={viewMode === 'list' ? 24 : 12} md={viewMode === 'list' ? 24 : 8} key={auction.id}>
                                        <div style={{ position: 'relative' }}>
                                            <AuctionCard
                                                auction={auction}
                                                viewMode={viewMode}
                                                onFavoriteToggle={handleFavoriteToggle}
                                            />
                                            {/* Real-time countdown overlay */}
                                            <AuctionCountdown
                                                auction={auction}
                                                variant="overlay"
                                            />
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                        
                        {/* Pagination */}
                        {pagination.total > 0 && (
                            <div style={{ 
                                marginTop: 32,
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '24px 0'
                            }}>
                                <Pagination
                                    current={pagination.current}
                                    pageSize={pagination.pageSize}
                                    total={pagination.total}
                                    onChange={handlePaginationChange}
                                    showSizeChanger={false}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            
            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-10px) rotate(180deg); }
                }
            `}</style>
        </div>
    );
};

export default AuctionList; 